import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  LinearProgress, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Alert, 
  CircularProgress, 
  Divider,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import io from 'socket.io-client';
import axios from 'axios';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ProgressValue = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  textAlign: 'right',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  switch (status) {
    case 'completed':
      color = theme.palette.success.main;
      break;
    case 'error':
      color = theme.palette.error.main;
      break;
    case 'generating':
    case 'packaging':
      color = theme.palette.info.main;
      break;
    default:
      color = theme.palette.grey[500];
  }
  
  return {
    backgroundColor: color,
    color: '#fff',
  };
});

/**
 * Progress Tracker Component
 * 
 * Displays real-time progress of theme generation
 * FIXED: Null safety for status.charAt() - prevents crashes when status is undefined
 */
const ProgressTracker = ({ projectId, initialStatus }) => {
  // State
  const [status, setStatus] = useState(initialStatus || {
    status: 'preparing',
    progress: 0,
    message: 'Preparing theme generation...'
  });
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Generation phases
  const phases = [
    { key: 'preparing', label: 'Preparing' },
    { key: 'generating', label: 'Generating Theme Files' },
    { key: 'packaging', label: 'Packaging Theme' },
    { key: 'complete', label: 'Complete' }
  ];
  
  // Connect to Socket.io for real-time updates
  useEffect(() => {
    let socket;
    
    const connectSocket = () => {
      try {
        // Connect to Socket.io server
        socket = io();
        
        // Subscribe to project updates
        socket.on('connect', () => {
          setConnected(true);
          socket.emit('subscribe', projectId);
          console.log('Socket.io connected');
        });
        
        // Handle progress updates
        socket.on('progress', (data) => {
          if (data.projectId === projectId) {
            setStatus({
              status: data.status,
              progress: data.progress,
              message: data.message
            });
          }
        });
        
        // Handle errors
        socket.on('error', (data) => {
          if (data.projectId === projectId) {
            setError(data.message);
            setStatus(prev => ({
              ...prev,
              status: 'error'
            }));
          }
        });
        
        // Handle disconnection
        socket.on('disconnect', () => {
          setConnected(false);
          console.log('Socket.io disconnected');
          
          // Start polling as fallback
          startPolling();
        });
        
        return socket;
      } catch (err) {
        console.error('Failed to connect to Socket.io:', err);
        setConnected(false);
        
        // Start polling as fallback
        startPolling();
        return null;
      }
    };
    
    // Start polling for updates
    const startPolling = () => {
      if (!pollingInterval) {
        const interval = setInterval(fetchStatus, 3000);
        setPollingInterval(interval);
      }
    };
    
    // Fetch status via API
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`/api/status/${projectId}`);
        
        if (response.data && response.data.project) {
          setStatus({
            status: response.data.project.status,
            progress: response.data.project.progress,
            message: response.data.project.message
          });
        }
      } catch (err) {
        console.error('Failed to fetch status:', err);
      }
    };
    
    // Initial status fetch
    fetchStatus();
    
    // Try to connect via Socket.io
    socket = connectSocket();
    
    // Cleanup
    return () => {
      if (socket) {
        socket.disconnect();
      }
      
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [projectId]);
  
  // Get current phase index
  const getCurrentPhaseIndex = () => {
    const index = phases.findIndex(phase => phase.key === status.status);
    return index >= 0 ? index : 0;
  };
  
  // Handle retry
  const handleRetry = async () => {
    try {
      setError(null);
      
      // Fetch current status
      const response = await axios.get(`/api/status/${projectId}`);
      
      if (response.data && response.data.project) {
        setStatus({
          status: response.data.project.status,
          progress: response.data.project.progress,
          message: response.data.project.message
        });
      }
    } catch (err) {
      console.error('Failed to retry:', err);
      setError('Failed to retry. Please try again.');
    }
  };
  
  return (
    <StyledPaper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Theme Generation Progress
        </Typography>
        
        <StatusChip 
          label={status.status ? status.status.charAt(0).toUpperCase() + status.status.slice(1) : 'Unknown'} 
          status={status.status}
          icon={status.status === 'completed' ? <CheckCircleIcon /> : null}
        />
      </Box>
      
      {!connected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Using polling for updates. Real-time updates not available.
        </Alert>
      )}
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      
      <Stepper activeStep={getCurrentPhaseIndex()} sx={{ mb: 3 }}>
        {phases.map((phase) => (
          <Step key={phase.key} completed={phases.indexOf(phase) < getCurrentPhaseIndex()}>
            <StepLabel>{phase.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <ProgressContainer>
        <LinearProgress 
          variant="determinate" 
          value={status.progress} 
          color={status.status === 'error' ? 'error' : 'primary'}
          sx={{ height: 10, borderRadius: 5 }}
        />
        <ProgressValue variant="body2" color="textSecondary">
          {status.progress}%
        </ProgressValue>
      </ProgressContainer>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        {status.status !== 'completed' && status.status !== 'error' && (
          <CircularProgress size={20} sx={{ mr: 2 }} />
        )}
        {status.status === 'completed' && (
          <CheckCircleIcon color="success" sx={{ mr: 2 }} />
        )}
        {status.status === 'error' && (
          <ErrorIcon color="error" sx={{ mr: 2 }} />
        )}
        <Typography variant="body1">
          {status.message || 'Processing...'}
        </Typography>
      </Box>
      
      {status.status === 'completed' && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Theme generation completed successfully!
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            href={`/api/download/${projectId}`}
            target="_blank"
          >
            Download Theme
          </Button>
        </Box>
      )}
    </StyledPaper>
  );
};

export default ProgressTracker;